/**
 * Writing: /blog (index) + /blog/:slug (article). Public + crawlable.
 * Content lives in src/data/articles.ts.
 *
 * Built because dev.to turned out to be banned site-wide on Reddit, so an
 * article that lives only on someone else's platform can be made unlinkable
 * without notice. This URL is ours.
 */
import { BrandMark } from '../components/BrandMark';
import { Link, useParams, Navigate } from 'react-router-dom';
import {
  articlesNewestFirst,
  getArticle,
  ArticleBlock,
  ARTICLE_AUTHOR,
  ARTICLE_AUTHOR_URL,
} from '../data/articles';
import { useSeo } from '../hooks/useSeo';

const SITE = 'https://agent-mesh.org';

/** 4 March 2026 → "4 March 2026". Dates are written out; nobody parses ISO by eye. */
function pretty(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  return `${d} ${months[m - 1]} ${y}`;
}

/**
 * The only markup the body supports: **bold** and `code`. Deliberately tiny —
 * a markdown dependency for two articles would be the CMS this was built to
 * avoid. Split on both patterns at once so they cannot nest wrongly.
 */
export function renderInline(text: string): React.ReactNode[] {
  return text
    .split(/(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g)
    .map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
        return (
          <code key={i} className="rounded bg-slate-100 px-1 py-0.5 text-[0.9em] text-slate-800">
            {part.slice(1, -1)}
          </code>
        );
      }
      // [text](url). Added 12 Sept 2026 because the model had NO way to express
      // a link, so converting the first article from markdown silently turned
      // six links into plain text — including the internal ones the link audit
      // had specifically asked for.
      const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(part);
      if (link) {
        const [, label, href] = link;
        // Only http(s) and site-relative. Anything else renders as text rather
        // than becoming an attribute — javascript: must never reach an href.
        const safe = /^(https?:\/\/|\/)/.test(href);
        if (!safe) return <span key={i}>{part}</span>;
        const external = href.startsWith('http');
        return (
          <a
            key={i}
            href={href}
            className="text-brand underline underline-offset-2 hover:no-underline"
            {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
          >
            {label}
          </a>
        );
      }
      return <span key={i}>{part}</span>;
    });
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <header className="border-b">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-4">
          <Link to="/blog" className="flex items-center gap-2">
            <BrandMark className="h-7 w-7" />
            <span className="text-lg font-bold">Agent<span className="text-brand">Mesh</span> Writing</span>
          </Link>
          <Link to="/" className="text-sm font-medium text-slate-600 hover:text-slate-900">
            agent-mesh.org
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-5 py-12">{children}</main>
      <footer className="border-t">
        <div className="mx-auto max-w-3xl px-5 py-8 text-sm text-slate-500">
          <Link to="/blog" className="hover:underline">All writing</Link> ·{' '}
          <Link to="/help" className="hover:underline">Help</Link> ·{' '}
          <Link to="/" className="hover:underline">Home</Link>
        </div>
      </footer>
    </div>
  );
}

function Block({ block }: { block: ArticleBlock }) {
  if ('h' in block) {
    return <h2 className="mt-10 mb-3 text-xl font-bold tracking-tight">{block.h}</h2>;
  }
  if ('h3' in block) {
    return <h3 className="mt-7 mb-2 text-lg font-bold tracking-tight">{block.h3}</h3>;
  }
  if ('p' in block) {
    return <p className="mb-4 leading-7 text-slate-700">{renderInline(block.p)}</p>;
  }
  if ('head' in block) {
    return (
      <div className="mb-5 overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr>
              {block.head.map((h, i) => (
                <th key={i} className="border-b px-3 py-2 text-left font-semibold">
                  {renderInline(h)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((r, i) => (
              <tr key={i}>
                {r.map((c, j) => (
                  <td key={j} className="border-b px-3 py-2 align-top text-slate-700">
                    {renderInline(c)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  if ('quote' in block) {
    return (
      <blockquote className="mb-4 border-l-4 border-slate-300 pl-4 text-slate-600 italic">
        <p className="leading-7">{renderInline(block.quote)}</p>
        {/* cite runs through renderInline too: a quote's attribution is exactly
            where a source link belongs, and it was the last block field that
            still printed raw text (found 12 Sept 2026). */}
        {block.cite && (
          <footer className="mt-1 text-sm not-italic text-slate-500">— {renderInline(block.cite)}</footer>
        )}
      </blockquote>
    );
  }
  if ('code' in block) {
    return (
      <pre className="mb-5 overflow-x-auto rounded-lg bg-[#0c1116] p-4 text-sm leading-6 text-slate-100">
        <code>
          {block.code.split('\n').map((line, i, all) => (
            <span key={i}>
              {/* A green tick is the subject of these articles, not decoration:
                  the whole point is five green checks that were all true and all
                  meaningless. Rendering them grey undersells the thing the piece
                  is about. Brand emerald, same as the mark. */}
              {line.startsWith('✓') ? (
                <>
                  {/* #10b981, not `text-brand`: --brand is 163 92% 26%, deliberately
                      darkened so it reads as text ON WHITE, which makes it nearly
                      invisible on this ink ground. This is the same emerald the
                      focus ring uses, and the exact value the prerenderer emits —
                      the two copies of the page must not differ. */}
                  <span className="text-[#10b981]">✓</span>
                  {line.slice(1)}
                </>
              ) : (
                line
              )}
              {i < all.length - 1 ? '\n' : ''}
            </span>
          ))}
        </code>
      </pre>
    );
  }
  if ('img' in block) {
    return (
      <figure className="mb-6">
        <img
          src={`/blog-assets/${block.img}`}
          alt={block.alt}
          loading="lazy"
          className="w-full rounded-lg border"
        />
        {block.caption && (
          <figcaption className="mt-2 text-sm text-slate-500">{renderInline(block.caption)}</figcaption>
        )}
      </figure>
    );
  }
  return (
    <ul className="mb-4 list-disc space-y-1 pl-6 leading-7 text-slate-700">
      {block.ul.map((li, i) => (
        <li key={i}>{renderInline(li)}</li>
      ))}
    </ul>
  );
}

export function BlogIndex() {
  useSeo({
    title: 'Writing',
    description:
      'Engineering write-ups from building AgentMesh: silent failures, deploys that report success without checking the effect, and what the runs actually record.',
    canonical: `${SITE}/blog`,
  });

  return (
    <Shell>
      {/* Everything between the h1 and the card list mirrors the /blog block in
          scripts/dev/gen-content-pages.mjs, which prerenders this page for
          crawlers. The index had no heading below the h1 at all until 12 Sept
          2026, and said nothing about what the posts have in common — which is
          the only reason to read a second one. Keep the two in step. */}
      <h1 className="mb-2 text-3xl font-bold">Writing</h1>
      <p className="mb-2 text-slate-600">
        Write-ups from building this thing. Mostly about checks that cannot report failure.
      </p>
      <p className="mb-8 text-sm text-slate-500">
        By{' '}
        <Link to="/about" className="text-brand hover:underline">{ARTICLE_AUTHOR}</Link> ·{' '}
        {articlesNewestFirst.length} posts · newest first
      </p>

      <h2 className="mb-2 text-xl font-bold">What these have in common</h2>
      <p className="mb-3 leading-7 text-slate-700">
        Every one is a failure that <strong>reported success</strong>. A run that finished{' '}
        <code>COMPLETED</code> having answered from data it never read. A deploy that printed five
        true green lines over a scheduler running two-week-old code. Twenty-seven workflow branches
        skipped while every run logged success.{' '}
        <strong>None of them threw an error, which is exactly why each one lasted weeks.</strong>
      </p>
      <p className="mb-8 leading-7 text-slate-700">
        They are written from this product's own runs, with the numbers left in. Source:{' '}
        <a href="/blog-assets/run-fff256c0.json" className="text-brand underline underline-offset-2">
          the raw execution record
        </a>{' '}
        behind the first one is published in full, and{' '}
        <Link to="/example" className="text-brand underline underline-offset-2">
          three runs are opened up step by step
        </Link>{' '}
        — $0.0144 for a worked support-ticket example, $0.0204 for a real logged run that also escalated, $0.0024 for one that
        failed early.
      </p>

      <h2 className="mb-2 text-xl font-bold">The pattern underneath all three</h2>
      <h3 className="mb-1 mt-4 font-bold">A status only knows whether something threw</h3>
      <p className="mb-3 leading-7 text-slate-700">
        Not whether the work happened. Every post here is the gap between those two, and it is the
        single most expensive thing I have found in this codebase.
      </p>
      <h3 className="mb-1 mt-4 font-bold">A check that cannot report failure is not a check</h3>
      <p className="mb-3 leading-7 text-slate-700">
        A health endpoint that answers. A test that acts and never asserts. A cleanup job that logs
        “removed 0” forever. Each is a green light wired to nothing.
      </p>
      <h3 className="mb-1 mt-4 font-bold">The count is the check</h3>
      <p className="mb-8 leading-7 text-slate-700">
        What survives is two-sided: the run says what it processed, the source says what it handed
        over, and the two have to tie out. A count of zero on a normally busy slot is a signal. A
        green status is not.
      </p>

      <h2 className="mb-4 text-xl font-bold">The posts</h2>
      <ul className="space-y-4">
        {articlesNewestFirst.map((a) => (
          <li key={a.slug}>
            <Link to={`/blog/${a.slug}`} className="block overflow-hidden rounded-lg border hover:border-slate-400">
              {a.cover && (
                <img src={`/blog-assets/${a.cover}`} alt={a.coverAlt ?? ''} className="w-full" />
              )}
              <div className="p-5">
              <time dateTime={a.published} className="text-sm text-slate-500">
                {pretty(a.published)}
              </time>
              <p className="mt-1 text-lg font-semibold">{a.title}</p>
              <p className="mt-1 text-sm text-slate-600">{a.summary}</p>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </Shell>
  );
}

export function BlogArticlePage() {
  const { slug = '' } = useParams();
  const article = getArticle(slug);

  useSeo({
    title: article ? article.metaTitle || article.title : 'Writing',
    description: article?.metaDescription || article?.summary,
    canonical: article ? `${SITE}/blog/${article.slug}` : `${SITE}/blog`,
    jsonLd: article
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: article.title,
            description: article.metaDescription || article.summary,
            url: `${SITE}/blog/${article.slug}`,
            author: { '@type': 'Person', name: ARTICLE_AUTHOR, url: ARTICLE_AUTHOR_URL },
            datePublished: article.published,
            dateModified: article.published,
            publisher: { '@type': 'Organization', name: 'AgentMesh', url: SITE },
            mainEntityOfPage: `${SITE}/blog/${article.slug}`,
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Writing', item: `${SITE}/blog` },
              {
                '@type': 'ListItem',
                position: 2,
                name: article.title,
                item: `${SITE}/blog/${article.slug}`,
              },
            ],
          },
        ]
      : undefined,
  });

  if (!article) return <Navigate to="/blog" replace />;

  return (
    <Shell>
      <article>
        <h1 className="mb-3 text-3xl font-bold tracking-tight">{article.title}</h1>
        {/* Must match the byline in scripts/dev/gen-content-pages.mjs — schema.org
            wants the marked-up author and date visible, and if only the prerendered
            copy carries it the two versions disagree. */}
        <p className="mb-8 text-sm text-slate-500">
          By{' '}
          <a href="/about" className="underline underline-offset-2">
            {ARTICLE_AUTHOR}
          </a>{' '}
          · <time dateTime={article.published}>{pretty(article.published)}</time>
        </p>

        {/* The cover, on the page and not only in the social card. `og` fills the
            meta tag; without this an article has a picture everywhere except on
            itself, which is how the first one published. */}
        {article.cover && (
          <img
            src={`/blog-assets/${article.cover}`}
            alt={article.coverAlt ?? ''}
            className="mb-8 w-full rounded-lg border"
          />
        )}

        {article.body.map((block, i) => (
          <Block key={i} block={block} />
        ))}
      </article>

      {article.mirrors && article.mirrors.length > 0 && (
        <p className="mt-10 border-t pt-6 text-sm text-slate-500">
          Also published on{' '}
          {article.mirrors.map((m, i) => (
            <span key={m.url}>
              {i > 0 && ' and '}
              <a
                className="text-brand hover:underline"
                href={m.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {m.label}
              </a>
            </span>
          ))}
          . This page is the original.
        </p>
      )}

      <div className="mt-8 border-t pt-6">
        <ul className="space-y-2 text-sm">
          {articlesNewestFirst
            .filter((a) => a.slug !== article.slug)
            .map((a) => (
              <li key={a.slug}>
                <Link to={`/blog/${a.slug}`} className="text-brand hover:underline">
                  {a.title}
                </Link>
              </li>
            ))}
          <li>
            <Link to="/blog" className="text-brand hover:underline">
              All writing →
            </Link>
          </li>
        </ul>
      </div>
    </Shell>
  );
}
